/**
 * Converts a snake_case string to camelCase
 * @example snakeToCamelCase('maintenance_logs') // 'maintenanceLogs'
 * @example snakeToCamelCase('service_package_versions') // 'servicePackageVersions'
 */
export function snakeToCamelCase(str: string): string {
  return str.replace(/_([a-z])/g, (_, letter) => letter.toUpperCase())
}

/**
 * Truncates a string to a specified length and appends an omission string
 * @example truncate('Hello World', { length: 8 }) // 'Hello...'
 * @example truncate('Hello World', { length: 8, omission: '…' }) // 'Hello…'
 * @example truncate('Hi', { length: 10 }) // 'Hi'
 */
export function truncate(
  str: string | null | undefined,
  options: { length?: number; omission?: string } = {},
): string {
  if (!str) return ''

  const { length = 30, omission = '...' } = options

  if (str.length <= length) return str

  const truncateAt = Math.max(0, length - omission.length)
  return str.slice(0, truncateAt) + omission
}

/**
 * Formats company name for payment systems (uppercase, dots to spaces)
 * Used in tilisiirtolomake, EPC QR codes, and wire transfer instructions
 * @example formatCompanyNameForPayment('Markku.fi Pientalohuolto Oy') // 'MARKKU FI PIENTALOHUOLTO OY'
 */
export function formatCompanyNameForPayment(name: string): string {
  return name.toUpperCase().replace(/\./g, ' ')
}

/**
 * Escapes SQL LIKE/ILIKE wildcard characters (% and _)
 * so user input is matched literally.
 * @example escapeLikePattern('100%') // '100\\%'
 */
export function escapeLikePattern(input: string): string {
  return input.replace(/[%_]/g, '\\$&')
}

/**
 * Normalizes an address for comparison by stripping whitespace and lowercasing.
 * Handles spaces in apartment letters (e.g. "1 B" vs "1B") and other formatting differences.
 * @example normalizeAddress({ street: 'Mäensyrjä 1 B', city: 'Espoo' }) // 'mäensyrjä1bespoo'
 */
export function normalizeAddress(addr: {
  street: string
  postalCode?: string | null
  city: string
}) {
  return `${addr.street}${addr.postalCode ?? ''}${addr.city}`
    .replace(/\s/g, '')
    .toLowerCase()
}
